import {forwardRef, Inject, Injectable} from '@angular/core';
import {ListService} from '@shared/models/list-service.model';
import {ContractsFilterModel} from '../models/contracts-filter.model';
import {Observable} from 'rxjs/Observable';
import {ContractsListModel} from '../models/contracts-list.model';
import {ClosedContractsModel} from '../models/closed-contracts-list.model';
import {EsbContractModel} from '@cc-commons/models/esb-contract.model';
import {Utilities} from '@shared/utilities/utilities.model';
import {HttpAuthService} from '@shared/service/http-auth.service';
import {ErrorResponseWrapperModel} from '@shared/models/error-response-wrapper.model';
import {CheckPreEvaluationModel} from '@cc-commons/models/check-pre-evaluation.model';
import {
	AttachedContractModel,
	ClaimListChangeLogInstance,
	ContractDetailsModel,
	CustomerContractModel,
	SurveyDamageModel,
	SurveyWearConditionModel,
	TokenListZip
} from '@cc-commons/models/contract-details.model';
import {Headers, RequestMethod, RequestOptions, RequestOptionsArgs, Response, ResponseContentType} from '@angular/http';
import {CCDashboardModel} from '@cc-commons/models/cc-dashboard.model';
import {UploadedContractsModel} from '@cc-commons/models/imported-contract-list.model';
import {SelectCommoditiesService} from '@shared/service/select-commodities.service';
import {UserV3Service} from '@shared/service/user-v-3.service';
import {AppConfigEnvironmentService} from '@shared/service/appconfig-environment.service';
import {AppConfigEnvironment} from '@shared/models/appconfig-environment.model';
import {CcReportAccessoriesModel} from '@cc-commons/models/cc-report-accessories.model';
import {throwError, zip} from 'rxjs';
import {concatMap} from 'rxjs/operators';
import {Appointment2Model, UpdateAppointment} from '../../appointments/models/appointment.model';
import {Appointments2Service} from '../../appointments/services/appointments2.service';
import {CcDkNewAppointmentModel} from '@shared/models/cc-dk-new-appointment.model';
import { MessageManagerUtilities } from '@shared/service/message-manager/message-manager-utilities';
import { ccContractDetailLog } from '@cc-commons/models/cc-contract-detail-log-list.model';
import { ErrorResponse } from '@shared/models/error-response.model';
import { HTTPStatusCode } from '@shared/models/http-status-code.model';


const CONTRACT_CLOSING_6104 = 'CONTRACT_CLOSING_6104';

@Injectable()
export class ContractsService implements ListService {
	public static TYPE_CERTIFICATE = 'certificate';
	public static TYPE_LETTER = 'letter';
	environment: AppConfigEnvironment;

	private static handlerResponseContractDetails(response, observer) {
		const errorResponseWrapper = new ErrorResponseWrapperModel();
		if (!Utilities.isVoid(response)) {
			if (response.status === 200) {
				observer.next(new ContractDetailsModel(response.json()));
			} else {
				errorResponseWrapper.responce_status = response.status;
				try {
					errorResponseWrapper.message = response.json();
				} catch { }

				observer.error(errorResponseWrapper);
			}
		} else {
			errorResponseWrapper.message = 'response is null';
			observer.error(errorResponseWrapper);
		}
	}

	static handlerError(error, observer) {
		const errorResponseWrapper = new ErrorResponseWrapperModel();
		errorResponseWrapper.message = 'js error: ' + error.toString();
		observer.error(errorResponseWrapper);
	}


	constructor(@Inject(forwardRef(() => HttpAuthService)) public httpAuthService: HttpAuthService,
				private userV3Service: UserV3Service,
				public appointmentService: Appointments2Service,
				private selectCommoditiesService: SelectCommoditiesService,
				private appConfig: AppConfigEnvironmentService) {
		this.environment = appConfig.Environment;

	}


	getDashboard(filter: ContractsFilterModel): Observable<CCDashboardModel> {
		let url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/dashboard';
		if (!Utilities.isVoid(filter)) {
			url = url + filter.getFilterUrl().replace(/\+/g, '%2B');
		}
		const commodities: string[] = this.selectCommoditiesService.getAldCommoditiesIds().map(id => id.toString());
		const options: RequestOptionsArgs = Utilities.setOptionsWithNemoCommodityID(commodities);
		return new Observable<CCDashboardModel>((observer) => {
			this.httpAuthService.get(url, this.environment.ROOT_REDIRECT, options)
				.subscribe(
					response => {
						const errorResponseWrapper = new ErrorResponseWrapperModel();
						if (!Utilities.isVoid(response)) {
							if (response.status === 200) {
								observer.next(new CCDashboardModel(response.json()));
							} else {
								errorResponseWrapper.responce_status = response.status;
								observer.error(errorResponseWrapper);
							}
						} else {
							errorResponseWrapper.message = 'response is null';
							observer.error(errorResponseWrapper);
						}
					}, error => {
						ContractsService.handlerError(error, observer);
					});
		});
	}

	getList(filter: ContractsFilterModel): Observable<ContractsListModel> {

		let url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts';
		if (filter ) {
			 const filterEncode=new ContractsFilterModel(filter);
			if(filter.closed_to && filter.closed_from){
				filterEncode.closed_to=encodeURIComponent(filterEncode.closed_to);
				filterEncode.closed_from=encodeURIComponent(filterEncode.closed_from);
			}
			url = url + (filterEncode.getFilterUrl()).replace(/\+/g, '%2B');
		}
		const commodities: string[] = this.selectCommoditiesService.getAldCommoditiesIds().map(id => id.toString());
		const options: RequestOptionsArgs = Utilities.setOptionsWithNemoCommodityID(commodities);
		return new Observable<ContractsListModel>((observer) => {
			this.httpAuthService.get(url, this.environment.ROOT_REDIRECT, options)
				.subscribe(
					response => {
						const errorResponseWrapper = new ErrorResponseWrapperModel();
						if (!Utilities.isVoid(response)) {
							if (response.status === 200) {
								observer.next(new ContractsListModel(response.json()));
							} else {
								errorResponseWrapper.responce_status = response.status;
								observer.error(errorResponseWrapper);
							}
						} else {
							errorResponseWrapper.message = 'response is null';
							observer.error(errorResponseWrapper);
						}
					}, error => {
						ContractsService.handlerError(error, observer);
					});
		});
	}

	getEsbAccessoriesByPlate(plate: string): Observable<CcReportAccessoriesModel[]> {
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/esb/contracts/' + plate + '/accessories';
		return new Observable<CcReportAccessoriesModel[]>((observer) => {
			this.httpAuthService.get(url, this.environment.ROOT_REDIRECT)
				.subscribe(
					response => {
						const errorResponseWrapper = new ErrorResponseWrapperModel();
						if (!Utilities.isVoid(response)) {
							if (response.status === 200) {
								observer.next(response.json() as CcReportAccessoriesModel[]);
							} else {
								errorResponseWrapper.responce_status = response.status;
								observer.error(errorResponseWrapper);
							}
						} else {
							errorResponseWrapper.message = 'response is null';
							observer.error(errorResponseWrapper);
						}
					}, error => {
						ContractsService.handlerError(error, observer);
					});
		});
	}


	getEsbContractByPlateOrContractNumberForDKC(plateOrContractNumber: string): Observable<EsbContractModel[]> {
        /*if (this.environment.environment === 'local') {
		     return new Observable<EsbContractModel[]>(
		         subscriber => {
                     // riconsegna semplice type RETURN: [{"plate":"ZB162AC","contract_number":"354127","vehicle_type":"Light Commercial Vehicle","model":"DEFENDER","make":"LAND ROVER","driver":"MASSIMI MARAZIA","contract_expiration":"2020-12-12T00:00:00+00:00","succeeding_contract_id":null,"type":"RETURN","delivery_location":null,"withdraw_at_the_office":null,"company":"400015","company_name":"TELECOM ITALIA SPA","already_imported":false,"start_date":"2012-12-13T00:00:00+00:00","expected_end_date":"2020-12-12T00:00:00+00:00","can_create_appointment":true,"appointment_id":null,"return_status":"Watch List from local country","return_service":"Perizia cont RMK – riacquisto no","cod_return_service":"401981"}]
                     // EXCHANGE_1 [{"plate":"DN642AA","contract_number":"DN642AA_1","vehicle_type":null,"model":"panda","make":"fiat","driver":"daniele  verdi","contract_expiration":null,"succeeding_contract_id":null,"type":"EXCHANGE1","delivery_location":null,"withdraw_at_the_office":null,"company":null,"company_name":null,"already_imported":true,"start_date":null,"expected_end_date":null,"can_create_appointment":true,"appointment_id":null,"return_status":null,"return_service":null,"cod_return_service":null},{"plate":"FM254DE","contract_number":"40195265","vehicle_type":"Passenger Car","model":"500X","make":"FIAT","driver":"POOL ","contract_expiration":"2019-12-17T12:00:00+00:00","succeeding_contract_id":null,"type":"DELIVERY","delivery_location":"414571","withdraw_at_the_office":null,"company":"400033","company_name":"Z.A. MANUTENZIONI EDILI","already_imported":false,"start_date":"2017-12-18T12:00:00+00:00","expected_end_date":"2019-12-17T00:00:00+00:00","can_create_appointment":true,"appointment_id":null,"return_status":"Active (Non-Fleet)","return_service":"Perizia cont RMK – riacquisto no","cod_return_service":"401981"}]
		             //subscriber.next(JSON.parse('[{"type":"RETURN","already_imported":"false","plate":"T123aa","model":"Ford Fiesta","driver":"Gino Latino","contract_number":"354180","vehicle_type":null,"contract_expiration":"2017-07-21T11:00:55+0000","delivery_location":null,"flag_preleasing":false,"can_create_appointment":true,"appointment_id":"null"},{"type":"DELIVERY","plate":"db273vk","model":"Ford focus","driver":"Gino Latino","contract_number":"40258362","vehicle_type":null,"contract_expiration":"2017-07-21T11:00:55+0000","delivery_location":"1000576","flag_preleasing":false}]') as EsbContractModel[]);
//                     subscriber.next(JSON.parse('[{"plate":"FL782BH","contract_number":"FL782BH_1","vehicle_type":null,"model":"500","make":"Fiat","driver":"Stefano Guglielmi","contract_expiration":null,"succeeding_contract_id":null,"type":"EXCHANGE1","delivery_location":null,"withdraw_at_the_office":null,"company":null,"company_name":null,"already_imported":true,"start_date":null,"expected_end_date":null,"can_create_appointment":false,"appointment_id":"a1e85e39-a471-4e45-9bd0-d30472710a5f","return_status":null,"return_service":null,"cod_return_service":null},{"plate":"FT987ET","contract_number":"40323372","vehicle_type":"Passenger Car","model":"TIGUAN","make":"VOLKSWAGEN","driver":"POOL ","contract_expiration":"2022-01-15T12:00:00+00:00","succeeding_contract_id":null,"type":"DELIVERY","delivery_location":"212402","withdraw_at_the_office":null,"company":"400033","company_name":"CARE DENT ITALIA SRL","already_imported":false,"start_date":"2019-01-16T12:00:00+00:00","expected_end_date":"2022-01-15T00:00:00+00:00","can_create_appointment":null,"appointment_id":null,"return_status":"Active (Fleet)","return_service":"401981","cod_return_service":"Perizia cont RMK – riacquisto no"}]') as EsbContractModel[]);
         //            subscriber.next(JSON.parse('[{"plate":"ED328TJ","contract_number":"ED328TJ_1","vehicle_type":null,"model":"modello","make":"marca","driver":"nome cognome","contract_expiration":null,"succeeding_contract_id":null,"type":"EXCHANGE1","delivery_location":null,"withdraw_at_the_office":null,"company":null,"company_name":null,"already_imported":true,"start_date":null,"expected_end_date":null,"can_create_appointment":false,"appointment_id":"58809363-e2d7-42a7-a3e7-8bbc4b5753f8","return_status":null,"return_service":null,"cod_return_service":null},{"plate":"FS528BE","contract_number":"40357326","vehicle_type":"Passenger Car","model":"500X","make":"FIAT","driver":"Roberto Lentino","contract_expiration":"2021-12-02T12:00:00+00:00","succeeding_contract_id":null,"type":"DELIVERY","delivery_location":null,"withdraw_at_the_office":null,"company":"400033","company_name":"Lentino Roberto","already_imported":false,"start_date":"2018-12-03T12:00:00+00:00","expected_end_date":"2021-12-02T00:00:00+00:00","can_create_appointment":true,"appointment_id":null,"return_status":"Active (Fleet)","return_service":"Perizia cont RMK – riacquisto no","cod_return_service":"401981"}]'));
         // delivery_location
                    subscriber.next(JSON.parse('[{"plate":"ER124JG","contract_number":"ER124JG_1","vehicle_type":null,"model":"Panda","make":"Fiat","driver":"Paolo Rossi","contract_expiration":null,"succeeding_contract_id":null,"type":"EXCHANGE1","delivery_location":null,"withdraw_at_the_office":null,"company":null,"company_name":null,"already_imported":true,"start_date":null,"expected_end_date":null,"can_create_appointment":false,"appointment_id":"e70eb2b9-e340-45ec-856f-ef34a7bb99b9","return_status":null,"return_service":null,"cod_return_service":null},{"plate":"FJ549GC","contract_number":"40085106","vehicle_type":"Passenger Car","model":"308","make":"PEUGEOT","driver":"POOL ","contract_expiration":"2020-05-11T00:00:00+00:00","succeeding_contract_id":null,"type":"DELIVERY","delivery_location":"369","withdraw_at_the_office":null,"company":"400033","company_name":"RISO VIAZZO SRL","already_imported":false,"start_date":"2017-05-12T00:00:00+00:00","expected_end_date":"2020-05-11T00:00:00+00:00","can_create_appointment":true,"appointment_id":null,"return_status":"Active (Fleet)","return_service":"Perizia cont RMK – riacquisto no","cod_return_service":"401981"}]'));
		         });
        }*/
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts/redelivery/' + plateOrContractNumber;
		return new Observable<EsbContractModel[]>((observer) => {
			this.httpAuthService.get(url, this.environment.ROOT_REDIRECT)
				.subscribe(
					response => {
						const errorResponseWrapper = new ErrorResponseWrapperModel();
						if (!Utilities.isVoid(response)) {
							if (response.status === 200) {
								observer.next(response.json() as EsbContractModel[]);
							} else {
								errorResponseWrapper.responce_status = response.status;
								observer.error(errorResponseWrapper);
							}
						} else {
							errorResponseWrapper.message = 'response is null';
							observer.error(errorResponseWrapper);
						}
					}, error => {
						ContractsService.handlerError(error, observer);
					});
		});
	}

	getEsbContractByPlateOrContractNumber(plateOrContractNumber: string): Observable<EsbContractModel> {
        /*
        if (this.environment.environment === 'local') {
            return new Observable<EsbContractModel>(
                subscriber => {
                    subscriber.next(JSON.parse('[{"plate":"ZB162AC","contract_number":"354127","vehicle_type":"Light Commercial Vehicle","model":"DEFENDER","make":"LAND ROVER","driver":"MASSIMI MARAZIA","contract_expiration":"2020-12-12T00:00:00+00:00","succeeding_contract_id":null,"type":null,"delivery_location":null,"withdraw_at_the_office":null,"company":"400015","company_name":"TELECOM ITALIA SPA","already_imported":false,"start_date":"2012-12-13T00:00:00+00:00","expected_end_date":"2020-12-12T00:00:00+00:00","can_create_appointment":null,"appointment_id":null,"return_status":"Watch List from local country","return_service":"Perizia cont RMK – riacquisto no","cod_return_service":"401981"}]') as EsbContractModel);
                });
        }
        */
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/esb/contracts/' + plateOrContractNumber;
		return new Observable<EsbContractModel>((observer) => {
			this.httpAuthService.get(url, this.environment.ROOT_REDIRECT)
				.subscribe(
					response => {
						const errorResponseWrapper = new ErrorResponseWrapperModel();
						if (!Utilities.isVoid(response)) {
							if (response.status === 200) {
								observer.next(response.json() as EsbContractModel);
							} else {
								errorResponseWrapper.responce_status = response.status;
								observer.error(errorResponseWrapper);
							}
						} else {
							errorResponseWrapper.message = 'response is null';
							observer.error(errorResponseWrapper);
						}
					}, error => {
						ContractsService.handlerError(error, observer);
					});
		});
	}

	createFirstExchange(body: any) {
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts/redelivery/exchange1/appointment';
        return this.httpAuthService.post(url, body, this.environment.ROOT_REDIRECT, null);
        /*
		return new Observable<ContractDetailsModel>((observer) => {
			this.httpAuthService.post(url, body, this.environment.ROOT_REDIRECT, null)
				.subscribe(
					response => {
						const errorResponseWrapper = new ErrorResponseWrapperModel();
						if (!Utilities.isVoid(response)) {
							if (response.status === 200) {
								observer.next(new ContractDetailsModel(response.json()));
							} else {
								errorResponseWrapper.responce_status = response.status;
								observer.error(errorResponseWrapper);
							}
						} else {
							errorResponseWrapper.message = 'response is null';
							observer.error(errorResponseWrapper);
						}
					}, error => {
						ContractsService.handlerError(error, observer);
					});
        });
        */
	}
	saveRebillableNote(rebillableNote: any, contractNumber: string) {
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/rebillable/' + contractNumber + '/insertRebillableNote';
        return this.httpAuthService.post(url, rebillableNote, this.environment.ROOT_REDIRECT, null);
	}
	createSecondExchange(body: any, plate: string) {
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts/redelivery/exchange2/' + plate + '/appointment';
        return this.httpAuthService.post(url, body, this.environment.ROOT_REDIRECT, null);
        /*
		return new Observable<Appointment2Model>((observer) => {
			this.httpAuthService.post(url, body, this.environment.ROOT_REDIRECT, null)
				.subscribe(
					response => {
						const errorResponseWrapper = new ErrorResponseWrapperModel();
						if (!Utilities.isVoid(response)) {
							if (response.status === 200) {
								observer.next(new Appointment2Model(response.json()));
							} else {
								errorResponseWrapper.responce_status = response.status;
								observer.error(errorResponseWrapper);
							}
						} else {
							errorResponseWrapper.message = 'response is null';
							observer.error(errorResponseWrapper);
						}
					}, error => {
						ContractsService.handlerError(error, observer);
					});
        });
        */
	}

	addContract(contractNumber: string, body: any): Observable<ContractDetailsModel> {
		/* if (this.environment.environment === 'local') {
			return new Observable<ContractDetailsModel>(
				subscriber => {
					subscriber.next(new ContractDetailsModel(JSON.parse(this.contract_with_survey_not_closed)));
				});
		} */
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts/' + contractNumber;
		return new Observable<ContractDetailsModel>((observer) => {
			this.httpAuthService.put(url, body, this.environment.ROOT_REDIRECT)
				.subscribe(
					response => {
						const errorResponseWrapper = new ErrorResponseWrapperModel();
						if (!Utilities.isVoid(response)) {
							if (response.status === 201) {
								observer.next(new ContractDetailsModel(response.json()));
							} else {
								errorResponseWrapper.responce_status = response.status;
								observer.error(errorResponseWrapper);
							}
						} else {
							errorResponseWrapper.message = 'response is null';
							observer.error(errorResponseWrapper);
						}
					}, error => {
						ContractsService.handlerError(error, observer);
					});
		});
	}

	getContractDetails(contractNumber: string): Observable<ContractDetailsModel> {
		// if (this.environment.environment === 'local') {
		// 	return new Observable<ContractDetailsModel>(
		// 		subscriber => {
		// 			subscriber.next(new ContractDetailsModel(JSON.parse(this.contract_ready_for_survey)));
		// 			subscriber.next(new ContractDetailsModel(JSON.parse(this.contract_ready_for_survey)));
		// 		});
		// }
		const commodities: string[] = this.selectCommoditiesService.getAldCommoditiesIds().map(id => id.toString());
		const options: RequestOptionsArgs = Utilities.setOptionsWithNemoCommodityID(commodities);
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts/' + contractNumber;
		return new Observable<ContractDetailsModel>((observer) => {
			this.httpAuthService.get(url, this.environment.ROOT_REDIRECT, options)
				.subscribe(
					response => {
						ContractsService.handlerResponseContractDetails(response, observer);
					}, error => {
						ContractsService.handlerError(error, observer);
					});
		});
	}

	private getTokenForCreateZipFile(attach: AttachedContractModel[]): Observable<Response> {
		const uuid: string[] = attach.map(a => a.uuid);
		const url: string = this.environment.UPLOAD_API + '/token/' + uuid.join();

		const options = Utilities.setRequestOptionAUTH();
		options.method = RequestMethod.Get;
		options.headers.set('Content-Type', 'application/json');
		options.headers.set('apikey', this.environment.UPLOAD_APIKEY);

		return this.httpAuthService.get(url, this.environment.ROOT_REDIRECT, options);
	}

	private getZipFromTokens(tokenList: TokenListZip[]): Observable<Response> {
		const uuid: string[] = tokenList.map(a => a.access_token);
		const url: string = this.environment.UPLOAD_API + '/blob/' + uuid.join() + '/download/zip';
		//const url: string = this.environment.UPLOAD_API + '/blob/' + uuid[0] + '/download';
		const options = Utilities.setRequestOptionAUTH();
		options.responseType = ResponseContentType.Blob;
		options.method = RequestMethod.Get;
		//options.headers.set('Content-Type', 'application/json');
		options.headers.set('apikey', this.environment.UPLOAD_APIKEY);
		return this.httpAuthService.get(url, this.environment.ROOT_REDIRECT, options)
	}

	public downloadZipFile(attach: AttachedContractModel[]): Observable<Response> {
		//return new Observable<any>((observer) => {
		return this.getTokenForCreateZipFile(attach).pipe(
			concatMap(
				response => {
					const errorResponseWrapper = new ErrorResponseWrapperModel();
					if (!Utilities.isVoid(response)) {
						if (response.status === 200) {
							return this.getZipFromTokens(response.json());
						} else {
							errorResponseWrapper.responce_status = response.status;
							return throwError(errorResponseWrapper);
						}
					} else {
						errorResponseWrapper.message = 'response is null';
						return throwError(errorResponseWrapper);
					}
				}
				// , error => {
				// 	const errResp = new ErrorResponseWrapperModel();
				// 	errResp.message = error;
				// 	Observable.throw(errResp);
			)
		);
	}

	setCheckReturned(contractNumber: string, check: CheckPreEvaluationModel): Observable<CheckPreEvaluationModel[]> {
		const type = check.check_id;
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts/' + contractNumber + '/checks_pre_evaluation/' + type + '/reenter';
		return new Observable<CheckPreEvaluationModel[]>((observer) => {
			this.httpAuthService.put(url, null, this.environment.ROOT_REDIRECT)
				.subscribe(
					response => {
						const errorResponseWrapper = new ErrorResponseWrapperModel();
						if (!Utilities.isVoid(response)) {
							if (response.status === 200) {
								const resp = response.json();
								const checks: CheckPreEvaluationModel[] = [];
								for (const checkPreEvaluation of resp.checks_pre_evaluation) {
									checks.push(new CheckPreEvaluationModel(checkPreEvaluation));
								}
								observer.next(checks);
							} else {
								errorResponseWrapper.responce_status = response.status;
								observer.error(errorResponseWrapper);
							}
						} else {
							errorResponseWrapper.message = 'response is null';
							observer.error(errorResponseWrapper);
						}
					}, error => {
						ContractsService.handlerError(error, observer);
					});
		});
	}

	setDamageReturned(contractNumber: string, damage: SurveyDamageModel): Observable<SurveyDamageModel[]> {
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts/' + contractNumber + '/damage/' + damage.id + '/reenter';
		return new Observable<SurveyDamageModel[]>((observer) => {
			this.httpAuthService.put(url, null, this.environment.ROOT_REDIRECT)
				.subscribe(
					response => {
						const errorResponseWrapper = new ErrorResponseWrapperModel();
						if (!Utilities.isVoid(response)) {
							if (response.status === 200) {
								const resp: SurveyWearConditionModel = response.json().wear_condition;
								let surveyDamageModel: SurveyDamageModel[] = [];
								surveyDamageModel = resp.external_damages.concat(resp.internal_damages);
								observer.next(surveyDamageModel);
							} else {
								errorResponseWrapper.responce_status = response.status;
								observer.error(errorResponseWrapper);
							}
						} else {
							errorResponseWrapper.message = 'response is null';
							observer.error(errorResponseWrapper);
						}
					}, error => {
						ContractsService.handlerError(error, observer);
					});
		});
	}

	saveCheckPreEvaluation(contractNumber: string, check: CheckPreEvaluationModel): Observable<CheckPreEvaluationModel> {
		/* if (this.environment.environment === 'local') {
			return new Observable<CheckPreEvaluationModel>(
				subscriber => {
					subscriber.next(new CheckPreEvaluationModel(check));
				});
		} */
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts/' + contractNumber + '/checks_pre_evaluation/' + check.check_id;
		return new Observable<CheckPreEvaluationModel>((observer) => {
			this.httpAuthService.put(url, check, this.environment.ROOT_REDIRECT)
				.subscribe(
					response => {
						const errorResponseWrapper = new ErrorResponseWrapperModel();
						if (!Utilities.isVoid(response)) {
							if (response.status === 200) {
								observer.next(new CheckPreEvaluationModel(response.json()));
							} else {
								errorResponseWrapper.responce_status = response.status;
								observer.error(errorResponseWrapper);
							}
						} else {
							errorResponseWrapper.message = 'response is null';
							observer.error(errorResponseWrapper);
						}
					}, error => {
						ContractsService.handlerError(error, observer);
					});
		});
	}

	setContractConfirmedAppointment(contractNumber: string, body: any) {
		/* if (this.environment.environment === 'local') {
			return new Observable<ContractDetailsModel>(
				subscriber => {
					subscriber.next(new ContractDetailsModel(JSON.parse(this.contract_appointment_confirmed)));
				});
		} */
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts/' + contractNumber + '/confirmAppointment';
		return new Observable<ContractDetailsModel>((observer) => {
			this.httpAuthService.post(url, body, this.environment.ROOT_REDIRECT)
				.subscribe(
					response => {
						ContractsService.handlerResponseContractDetails(response, observer);
					}, error => {
						ContractsService.handlerError(error, observer);
					});
		});
	}

	createAppointmentDekraAndDelivery(closingAppointment: CcDkNewAppointmentModel, deliveryAppointment: Appointment2Model) {
		let closing: any;
		if (closingAppointment.appointment.type === Appointment2Model.APPOINTMENT_TYPE_EXCHANGE_1
			|| closingAppointment.appointment.type === Appointment2Model.APPOINTMENT_TYPE_EXCHANGE_2) {
			closing = this.createAppointmentDekraRedelivery(closingAppointment);
		} else {
			closing = this.createAppointmentDekra(closingAppointment.appointment);
		}
		const delivery = this.createDeliveryAppointment(deliveryAppointment);
		return zip(closing, delivery);
	}

	createDeliveryAppointment(appointment: Appointment2Model): Observable<Response> {
		return this.appointmentService.newAppointment(appointment);
	}

	createAppointmentDekra(appointment: Appointment2Model) {
		const contractNumber = appointment.contract.ald_order_id;
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts/' + contractNumber + '/appointment';
		return this.httpAuthService.post(url, appointment, this.environment.ROOT_REDIRECT, null);
	}

	createAppointmentDekraRedelivery(appointment: CcDkNewAppointmentModel) {
		//TODO: gestire chiamate chiedere a Dario!!
		const contractNumber = appointment.appointment.contract.ald_order_id;
		let url: string = this.environment.NEMO_CONTRACT_CLOSING_API;
		if (appointment.appointment.type === Appointment2Model.APPOINTMENT_TYPE_EXCHANGE_1) {
			url = url + '/contracts/' + contractNumber + '/appointment/redelivery';
		} else {
			url = url + '/contracts/' + contractNumber + '/appointment/redelivery';
		}
		return this.httpAuthService.post(url, appointment, this.environment.ROOT_REDIRECT, null);
	}

	updateAppointmentDekra(appointment: UpdateAppointment, contractNumber: string) {
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts/' + contractNumber + '/appointment';
		return this.httpAuthService.put(url, appointment, this.environment.ROOT_REDIRECT, null);
	}

	setContractReadyForSurvey(contractNumber: string, body: any) {
		/* if (this.environment.environment === 'local') {
			return new Observable<ContractDetailsModel>(
				subscriber => {
					subscriber.next(new ContractDetailsModel(JSON.parse(this.contract_ready_for_survey)));
				});
		} */
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts/' + contractNumber + '/readyForSurvey';
		return new Observable<ContractDetailsModel>((observer) => {
			this.httpAuthService.post(url, body, this.environment.ROOT_REDIRECT)
				.subscribe(
					response => {
						ContractsService.handlerResponseContractDetails(response, observer);
					}, error => {
						ContractsService.handlerError(error, observer);
					});
		});
	}

	saveSurvey(contractNumber: string, contractDetails: ContractDetailsModel): Observable<ContractDetailsModel> {
		/*
		if (this.environment.environment === 'local') {
			return new Observable<ContractDetailsModel>(
				subscriber => {
					subscriber.next(contractDetails);
				});
		}*/
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts/' + contractNumber + '/survey';
		return new Observable<ContractDetailsModel>((observer) => {
			this.httpAuthService.post(url, contractDetails.wear_condition.getBody(), this.environment.ROOT_REDIRECT)
				.subscribe(
					response => {
						ContractsService.handlerResponseContractDetails(response, observer);
					}, error => {
						ContractsService.handlerError(error, observer);
					});
		});
	}

	setContractWithSurvey(contractNumber: string): Observable<ContractDetailsModel> {
		/*
		if (this.environment.environment === 'local') {
			return new Observable<ContractDetailsModel>(
				subscriber => {
					subscriber.next(new ContractDetailsModel(JSON.parse(this.contract_witt_survey_closed)));
				});
		}
		*/
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts/' + contractNumber + '/surveyDone';
		return new Observable<ContractDetailsModel>((observer) => {
			this.httpAuthService.post(url, null, this.environment.ROOT_REDIRECT)
				.subscribe(
					response => {
						ContractsService.handlerResponseContractDetails(response, observer);
					}, error => {
						ContractsService.handlerError(error, observer);
					});
		});
	}

	setContractNoShow(contractNumber: string, body: any): Observable<ContractDetailsModel> {
		/* if (this.environment.environment === 'local') {
			return new Observable<ContractDetailsModel>(
				subscriber => {
					subscriber.next(new ContractDetailsModel(JSON.parse(this.contract_witt_survey_closed)));
				});
		} */
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts/' + contractNumber + '/no_show';
		return new Observable<ContractDetailsModel>((observer) => {
			this.httpAuthService.post(url, body, this.environment.ROOT_REDIRECT)
				.subscribe(
					response => {
						ContractsService.handlerResponseContractDetails(response, observer);
					}, error => {
						ContractsService.handlerError(error, observer);
					});
		});
	}

	setContractToDeclined(contractNumber: string): Observable<ContractDetailsModel> {
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts/' + contractNumber;
		return new Observable<ContractDetailsModel>((observer) => {
			this.httpAuthService.delete(url,this.environment.ROOT_REDIRECT)
				.subscribe(
					response => {
						ContractsService.handlerResponseContractDetails(response, observer);
					}, error => {
						ContractsService.handlerError(error, observer);
					});
		});
	}

	setContractClosed(contractNumber: string): Observable<ContractDetailsModel> {
		/* if (this.environment.environment === 'local') {
			return new Observable<ContractDetailsModel>(
				subscriber => {
					subscriber.next(new ContractDetailsModel(JSON.parse(this.contract_witt_survey_closed)));
				});
		} */
        const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts/' + contractNumber + '/close';
        const body = {
			closure_note: ''
		};
		return new Observable<ContractDetailsModel>((observer) => {
			this.httpAuthService.post(url, body, this.environment.ROOT_REDIRECT)
				.subscribe(
					response => {
						ContractsService.handlerResponseContractDetails(response, observer);
					}, error => {
						ContractsService.handlerError(error, observer);
					});
		});
	}

	setContractClosedWithNote(contractNumber: string,note:string): Observable<ContractDetailsModel> {
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts/' + contractNumber + '/close';
		const body = {
			closure_note: note
		};
		return new Observable<ContractDetailsModel>((observer) => {
			this.httpAuthService.post(url, body, this.environment.ROOT_REDIRECT)
				.subscribe(
					response => {

						const res: any = response;
						if (res.poolfleet_exception && res.poolfleet_exception.code === CONTRACT_CLOSING_6104) {	
							ContractsService.handlerError(res.poolfleet_exception, observer);
							return;
						}

						ContractsService.handlerResponseContractDetails(response, observer);
					}, error => {
						ContractsService.handlerError(error, observer);
					});
		});
	}

	setContractClosedByDigitalSign(contractNumber: string): Observable<ContractDetailsModel> {
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts/' + contractNumber + '/close_digitally_signed';
		return new Observable<ContractDetailsModel>((observer) => {
			this.httpAuthService.post(url, null, this.environment.ROOT_REDIRECT)
				.subscribe(
					response => {

						const res: any = response;
						if (res.poolfleet_exception && res.poolfleet_exception.code === CONTRACT_CLOSING_6104) {	
							ContractsService.handlerError(res.poolfleet_exception, observer);
							return;
						}

						ContractsService.handlerResponseContractDetails(response, observer);
					}, error => {
						ContractsService.handlerError(error, observer);
					});
		});
	}

	insertOrUpdateAttachments(contractNumber: string, attachments: AttachedContractModel[]): Observable<ContractDetailsModel> {
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts/' + contractNumber + '/attachments';
		const body = {
			attachments: attachments
		};
		return new Observable<ContractDetailsModel>((observer) => {
			this.httpAuthService.post(url, body, this.environment.ROOT_REDIRECT)
				.subscribe(
					response => {
						ContractsService.handlerResponseContractDetails(response, observer);
					}, error => {
						ContractsService.handlerError(error, observer);
					});
		});
	}

	removeAttachments(contractNumber: string, attachments: AttachedContractModel[]): Observable<ContractDetailsModel> {
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts/' + contractNumber + '/attachments';
		const options = new RequestOptions({
			body: {
				attachments: attachments
			}
		});
		return new Observable<ContractDetailsModel>((observer) => {
			this.httpAuthService.delete(url, this.environment.ROOT_REDIRECT, options)
				.subscribe(
					response => {
						ContractsService.handlerResponseContractDetails(response, observer);
					}, error => {
						ContractsService.handlerError(error, observer);
					});
		});
	}

	saveCustomer(contractNumber: string, customer: CustomerContractModel): Observable<ContractDetailsModel> {
		/* if (this.environment.environment === 'local') {
			return new Observable<ContractDetailsModel>(
				subscriber => {
					subscriber.next(new ContractDetailsModel(JSON.parse(this.contract_with_survey_closed_and_customer_saved)));
				});
		} */
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts/' + contractNumber + '/customer';
		return new Observable<ContractDetailsModel>((observer) => {
			this.httpAuthService.post(url, customer.getBody(), this.environment.ROOT_REDIRECT)
				.subscribe(
					response => {
						ContractsService.handlerResponseContractDetails(response, observer);
					}, error => {
						ContractsService.handlerError(error, observer);
					});
		});
	}

	parseContractsFromFile(fileBase64: string): Observable<UploadedContractsModel> {
		const body = {
			file: fileBase64
		};
		const user = this.userV3Service.getCurrentUser().id;
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/file/contracts';
		const options = new RequestOptions();
		options.headers = new Headers();
		options.headers.append('nemo-user-id', user );

		return new Observable<UploadedContractsModel>((observer) => {
			  this.httpAuthService.post(url, body, this.environment.ROOT_REDIRECT, options)
				.subscribe(
					response => {
						const errorResponseWrapper = new ErrorResponseWrapperModel();
						if (!Utilities.isVoid(response)) {
							if (response.status === 200) {
								const value = response.json();
								if (!Utilities.isVoid(value)) {
									observer.next(value as UploadedContractsModel);
								} else {
									errorResponseWrapper.responce_status = response.status;
									errorResponseWrapper.message = 'no body';
									observer.error(errorResponseWrapper);
								}
							} else {
								errorResponseWrapper.responce_status = response.status;
								observer.error(errorResponseWrapper);
							}
						} else {
							errorResponseWrapper.message = 'response is null';
							observer.error(errorResponseWrapper);
						}
					}, error => {
						ContractsService.handlerError(error, observer);
					});

		});
	}


	importContractsFromFile(contracts: UploadedContractsModel): Observable<ClosedContractsModel> {
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/file/contracts/asyncupload';
		const user = this.userV3Service.getCurrentUser().id;
		const options = new RequestOptions();
		options.headers = new Headers();
		options.headers.append('nemo-user-id', user );
		return new Observable<ClosedContractsModel>((observer) => {
			this.httpAuthService.post(url, contracts, this.environment.ROOT_REDIRECT,options)
				.subscribe(
					response => {
						const errorResponseWrapper = new ErrorResponseWrapperModel();
						if (!Utilities.isVoid(response)) {
							if (response.status === 200) {
								const value = response.json();
								if (!Utilities.isVoid(value)) {
									observer.next(value as ClosedContractsModel);
								} else {
									errorResponseWrapper.responce_status = response.status;
									errorResponseWrapper.message = 'no body';
									observer.error(errorResponseWrapper);
								}
							} else {
								errorResponseWrapper.responce_status = response.status;
								observer.error(errorResponseWrapper);
							}
						} else {
							errorResponseWrapper.message = 'response is null';
							observer.error(errorResponseWrapper);
						}
					}, error => {
						ContractsService.handlerError(error, observer);
					});
		});
	}


	printDocument(contractNumber: string): Observable<string> {
		if (this.environment.environment === 'aaa') {
			return new Observable<string>(
				subscriber => {
					subscriber.next('40315760');
				});
		} else {
			const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts/' + contractNumber + '/print';
			return new Observable<string>((observer) => {
				this.httpAuthService.get(url, this.environment.ROOT_REDIRECT)
					.subscribe(
						response => {
							const errorResponseWrapper = new ErrorResponseWrapperModel();
							if (!Utilities.isVoid(response)) {
								if (response.status === 200) {
									const json = response.json();
									if (!Utilities.isVoid(json) && !Utilities.isVoid(json.request)
										&& !Utilities.isVoid(json.request.filemanager) && !Utilities.isVoid(json.request.filemanager.uuid)) {
										observer.next(json.request.filemanager.uuid);
									} else {
										errorResponseWrapper.responce_status = response.status;
										errorResponseWrapper.message = 'response doesn\'t have uuid';
										observer.error(errorResponseWrapper);
									}
								} else {
									errorResponseWrapper.responce_status = response.status;
									observer.error(errorResponseWrapper);
								}
							} else {
								errorResponseWrapper.message = 'response is null';
								observer.error(errorResponseWrapper);
							}
						}, error => {
							ContractsService.handlerError(error, observer);
						});
			});
		}
	}

	checkContractsStatus(): Observable<ClosedContractsModel>{
		const user = this.userV3Service.getCurrentUser().id;
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API +'/file/contracts/polling';
		const options = new RequestOptions();
		options.headers = new Headers();
		options.headers.append('nemo-user-id', user );
		return new Observable<ClosedContractsModel>((observer) => {
			this.httpAuthService.get(url, this.environment.ROOT_REDIRECT,options)
				.subscribe(
					response => {
						const errorResponseWrapper = new ErrorResponseWrapperModel();
						if (!Utilities.isVoid(response)) {
							if (response.status === 200) {
								const value = response.json();
								observer.next(value as ClosedContractsModel);
							} else {
								errorResponseWrapper.responce_status = response.status;
								observer.error(errorResponseWrapper);
							}
						} else {
							errorResponseWrapper.message = 'response is null';
							observer.error(errorResponseWrapper);
						}
					}, error => {
						ContractsService.handlerError(error, observer);
					});
        });
    }

	exportFile(filter:ContractsFilterModel):Observable<any>{
		let url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts/export';
		if (filter) {
			url = url + filter.getFilterUrl().replace(/\+/g, '%2B');
		}
        return this.httpAuthService.get(url,this.environment.ROOT_REDIRECT);
  }

	sendNewEmail(contractNumber:string): Observable<string> {
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/rebillable/' + contractNumber + '/email';
		return new Observable<string>((observer) => {
			this.httpAuthService.post(url, {}, this.environment.ROOT_REDIRECT)
				.subscribe(
					response => {
						ContractsService.handlerResponseContractDetails(response, observer);
					}, error => {
						ContractsService.handlerError(error, observer);
					});
		});
	}

	saveContractAmounts(obj: ClaimListChangeLogInstance): Observable<ClaimListChangeLogInstance> {
		// Salva gli importi separatamente nel dettaglio del contratto

		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/rebillable/' + obj.contract + '/updateAdzPartCost';
		return new Observable<ClaimListChangeLogInstance>((observer) => {
			this.httpAuthService.post(url, obj, this.environment.ROOT_REDIRECT)
				.subscribe(
					resp => {

						if (resp.status === 204 || resp.status === 200) {
							observer.next(obj);
							return;
						}
						
						observer.next({} as ClaimListChangeLogInstance);

					}, error => {
						ContractsService.handlerError(error, observer);
					});
		});
	}

	getChangeLogContractAmounts(contractNumber: string): Observable<ClaimListChangeLogInstance[]> {
		// GET storico modifiche importi
	
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/rebillable/' + contractNumber + '/getAdzPartCostLogs';
		return new Observable<ClaimListChangeLogInstance[]>((observer) => {
			this.httpAuthService.get(url, this.environment.ROOT_REDIRECT)
				.subscribe(
					resp => {

						if (resp.status === 200) {
							observer.next(resp.json() as ClaimListChangeLogInstance[]);
							return;
						}
						
						observer.next([] as ClaimListChangeLogInstance[]);

					}, error => {
						ContractsService.handlerError(error, observer);
					});
		});
	}

	getContractDetailLogs(contractNumber: string): Observable<ccContractDetailLog[]> {
		// GET storico eventi contratto
		const url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts/' + contractNumber + '/logs';
		return new Observable<ccContractDetailLog[]>((observer) => {
			this.httpAuthService.get(url, this.environment.ROOT_REDIRECT)
				.subscribe(
					resp => {
						if (resp.status === 200) {
							observer.next(resp.json() as ccContractDetailLog[]);
							return;
						}

						observer.error(MessageManagerUtilities.getMessageDetailsFromResponse(resp));
					}, error => {
						observer.error(MessageManagerUtilities.getMessageDetailsFromResponse(error));
					});
		});
	}

	reactiveContract(contractNumber: string, local: any) {
		let url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts/' + contractNumber + '/reactive';
		let queryParams = {
			call_miles: local ? false : true
		}
		url = url + Utilities.getQuery(queryParams)
		return new Observable<any>((observer) => {
			this.httpAuthService.post(url, {}, this.environment.ROOT_REDIRECT)
				.subscribe(
					response => {
						const errorResponseWrapper = new ErrorResponseWrapperModel();
						if (!Utilities.isVoid(response)) {
							if (response.status === 200) {
								observer.next(response);
							} else {
								observer.error(MessageManagerUtilities.getMessageDetailsFromResponse(response));
							}
						} else {
							observer.error(MessageManagerUtilities.getMessageDetailsFromResponse(response));
						}
					}, error => {
						observer.error(MessageManagerUtilities.getMessageDetailsFromResponse(error));
					});
		});
	}

	updateClosingData(contractNumber: string, body: any,  local: any) {
		let url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts/' + contractNumber + '/update_data_closing';
		let queryParams = {
			call_miles: local ? false : true
		}
		url = url + Utilities.getQuery(queryParams)
		return new Observable<any>((observer) => {
			this.httpAuthService.put(url, body, this.environment.ROOT_REDIRECT)
				.subscribe(
					response => {
						const errorResponseWrapper = new ErrorResponseWrapperModel();
						if (!Utilities.isVoid(response)) {
							if (response.status === 200) {
								observer.next(response);
							} else {
								observer.error(MessageManagerUtilities.getMessageDetailsFromResponse(response));
							}
						} else {
							observer.error(MessageManagerUtilities.getMessageDetailsFromResponse(response));
						}
					}, error => {
						observer.error(MessageManagerUtilities.getMessageDetailsFromResponse(error));
					});
		});
	}

	insertPo(contractNumber: string, body: any = {}) {
		let url: string = this.environment.NEMO_CONTRACT_CLOSING_API + '/contracts/' + contractNumber + '/insert_po';
		return new Observable<any>((observer) => {
			this.httpAuthService.post(url, body, this.environment.ROOT_REDIRECT)
				.subscribe(
					res => {
						const content = res.json();
						if (res.status === HTTPStatusCode[200]) {
							try {
								observer.next(new ContractDetailsModel(content));
							} catch (err) {
								console.error(err);
								observer.error(err);
							}
						} else {

							const err = new ErrorResponse(res);

							if (err.hasStatusCode([HTTPStatusCode[404], HTTPStatusCode[500]])) {
								console.error(err);
								observer.error(err);
							}	
							
							observer.next(null);
						}
					},
					err => {
						console.error(err);
						observer.error(err);
					}
				);
		});
	}
}
